import * as React from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';

import { cn } from '@common/lib/utils';
import { Button } from '@common/components/ui/button';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@common/components/ui/input-group';

const Combobox = ComboboxPrimitive.Root;

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
}

function ComboboxTrigger({
  className,
  children,
  ...props
}: ComboboxPrimitive.Trigger.Props) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cn('[&_svg:not([class*=size-])]:tw-size-4', className)}
      {...props}
    >
      {children}
      <ChevronDownIcon className="tw-pointer-events-none tw-size-4 tw-text-muted-foreground" />
    </ComboboxPrimitive.Trigger>
  );
}

function ComboboxClear({ className, ...props }: ComboboxPrimitive.Clear.Props) {
  return (
    <ComboboxPrimitive.Clear
      data-slot="combobox-clear"
      className={cn(className)}
      {...props}
      render={<Button type="button" variant="ghost" size="icon" className="!tw-size-6 !tw-min-w-0 !tw-p-0" />}
    >
      <XIcon className="tw-pointer-events-none tw-size-4" />
    </ComboboxPrimitive.Clear>
  );
}

function ComboboxInput({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  ...props
}: ComboboxPrimitive.Input.Props & {
  showTrigger?: boolean;
  showClear?: boolean;
}) {
  return (
    <InputGroup className={cn('tw-w-auto', className)}>
      <ComboboxPrimitive.Input
        render={<InputGroupInput disabled={disabled} />}
        {...props}
      />
      <InputGroupAddon align="inline-end">
        {showTrigger && (
          <InputGroupButton
            size="icon-xs"
            variant="ghost"
            asChild
            data-slot="input-group-button"
            className="group-has-[[data-slot=combobox-clear]]/input-group:tw-hidden"
            disabled={disabled}
          >
            <ComboboxTrigger />
          </InputGroupButton>
        )}
        {showClear && <ComboboxClear disabled={disabled} />}
      </InputGroupAddon>
      {children}
    </InputGroup>
  );
}

function ComboboxContent({
  className,
  side = 'bottom',
  sideOffset = 6,
  align = 'start',
  alignOffset = 0,
  anchor,
  container,
  collisionBoundary,
  collisionAvoidance,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<
    ComboboxPrimitive.Positioner.Props,
    | 'side'
    | 'align'
    | 'sideOffset'
    | 'alignOffset'
    | 'anchor'
    | 'collisionBoundary'
    | 'collisionAvoidance'
  > & {
    container?: ComboboxPrimitive.Portal.Props['container'];
  }) {
  return (
    <ComboboxPrimitive.Portal container={container}>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        collisionBoundary={collisionBoundary}
        collisionAvoidance={collisionAvoidance}
        className="tw-isolate tw-z-[80]"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={cn(
            `tw-group/combobox-content tw-bg-popover tw-text-popover-foreground tw-relative tw-max-h-[var(--available-height)]
            tw-w-[var(--anchor-width)] tw-max-w-[var(--available-width)] tw-min-w-[var(--anchor-width)]
            tw-origin-[var(--transform-origin)] tw-overflow-hidden tw-rounded-md tw-border tw-border-border tw-shadow-md
            tw-outline-none data-[side=bottom]:tw-slide-in-from-top-2 data-[side=top]:tw-slide-in-from-bottom-2
            data-[side=left]:tw-slide-in-from-right-2 data-[side=right]:tw-slide-in-from-left-2
            data-[open]:tw-animate-in data-[open]:tw-fade-in-0 data-[open]:tw-zoom-in-95
            data-[closed]:tw-animate-out data-[closed]:tw-fade-out-0 data-[closed]:tw-zoom-out-95`,
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn(
        'tw-max-h-[288px] tw-scroll-py-1 tw-overflow-y-auto tw-overscroll-contain tw-p-1 data-[empty]:tw-p-0',
        className,
      )}
      {...props}
    />
  );
}

function ComboboxItem({
  className,
  children,
  ...props
}: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        `tw-relative tw-flex tw-w-full tw-cursor-default tw-items-center tw-gap-2 tw-rounded-sm tw-py-1.5 tw-pr-8 tw-pl-2
        tw-text-sm tw-outline-none tw-select-none data-[highlighted]:tw-bg-accent data-[highlighted]:tw-text-accent-foreground
        data-[disabled]:tw-pointer-events-none data-[disabled]:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-shrink-0
        [&_svg:not([class*=size-])]:tw-size-4`,
        className,
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator
        data-slot="combobox-item-indicator"
        className="tw-pointer-events-none tw-absolute tw-right-2 tw-flex tw-size-4 tw-items-center tw-justify-center"
      >
        <CheckIcon className="tw-pointer-events-none tw-size-4" />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  );
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return (
    <ComboboxPrimitive.Group
      data-slot="combobox-group"
      className={cn(className)}
      {...props}
    />
  );
}

function ComboboxLabel({
  className,
  ...props
}: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={cn('tw-px-2 tw-py-1.5 tw-text-xs tw-text-muted-foreground', className)}
      {...props}
    />
  );
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
  return (
    <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />
  );
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        'tw-w-full tw-justify-center tw-py-2 tw-text-center tw-text-sm tw-text-muted-foreground empty:tw-hidden empty:tw-py-0',
        className,
      )}
      {...props}
    />
  );
}

function ComboboxSeparator({
  className,
  ...props
}: ComboboxPrimitive.Separator.Props) {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={cn('tw--mx-1 tw-my-1 tw-h-px tw-bg-border', className)}
      {...props}
    />
  );
}

const ComboboxChips = React.forwardRef<HTMLDivElement, ComboboxPrimitive.Chips.Props>(
  ({ className, ...props }, ref) => {
    return (
      <ComboboxPrimitive.Chips
        ref={ref}
        data-slot="combobox-chips"
        className={cn(
          `tw-flex tw-min-h-8 tw-w-full tw-flex-wrap tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-input
          tw-bg-transparent tw-px-2.5 tw-py-1 tw-text-sm tw-transition-colors focus-within:tw-ring-1 focus-within:tw-ring-ring
          has-[[data-slot=combobox-chip]]:tw-px-1`,
          className,
        )}
        {...props}
      />
    );
  },
);

function ComboboxChip({
  className,
  children,
  showRemove = true,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean;
}) {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cn(
        `tw-flex tw-h-5 tw-w-fit tw-items-center tw-justify-center tw-gap-1 tw-whitespace-nowrap tw-rounded-sm
        tw-bg-muted tw-px-1.5 tw-text-xs tw-font-medium tw-text-foreground
        has-[:disabled]:tw-pointer-events-none has-[:disabled]:tw-cursor-not-allowed has-[:disabled]:tw-opacity-50
        has-[[data-slot=combobox-chip-remove]]:tw-pr-0`,
        className,
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          className="tw--ml-1 tw-opacity-50 hover:tw-opacity-100"
          data-slot="combobox-chip-remove"
          render={<Button type="button" variant="ghost" size="icon" className="!tw-size-4 !tw-min-w-0 !tw-p-0" />}
        >
          <XIcon className="tw-pointer-events-none tw-size-3" />
        </ComboboxPrimitive.ChipRemove>
      )}
    </ComboboxPrimitive.Chip>
  );
}

function ComboboxChipsInput({
  className,
  ...props
}: ComboboxPrimitive.Input.Props) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      className={cn('tw-min-w-16 tw-flex-1 tw-bg-transparent tw-outline-none', className)}
      {...props}
    />
  );
}

function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null);
}

export {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxGroup,
  ComboboxLabel,
  ComboboxCollection,
  ComboboxEmpty,
  ComboboxSeparator,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
  ComboboxTrigger,
  ComboboxValue,
  ComboboxClear,
  useComboboxAnchor,
};
